import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, RefreshCw, AlertTriangle, Activity, Users, Server,
  Clock, CheckCircle, XCircle, Loader2, ChevronDown, Wifi, WifiOff
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'errors', label: 'Error Log', icon: AlertTriangle },
  { id: 'jobs', label: 'Active Jobs', icon: Activity },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'system', label: 'System', icon: Server },
];

const TYPE_COLORS = {
  asset_error: '#F59E0B',
  sketch_error: '#8B5CF6',
  video_error: '#EF4444',
  recipe_error: '#EC4899',
  server_error: '#F97316',
  api_error: '#06B6D4',
};

const SEVERITY_COLORS = {
  error: '#EF4444',
  warning: '#F59E0B',
  info: '#6B7280',
};

function timeAgo(ts) {
  if (!ts) return 'never';
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function AdminDebugPanel() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('errors');
  const [health, setHealth] = useState(null);
  const [logs, setLogs] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [logFilter, setLogFilter] = useState({ type: '', hours: 24 });
  const [expandedLog, setExpandedLog] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchHealth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/system-health`, { headers });
      setHealth(data);
    } catch (err) {
      console.error('Health fetch failed:', err);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const params = { hours: logFilter.hours, limit: 200 };
      if (logFilter.type) params.type = logFilter.type;
      const { data } = await axios.get(`${API}/api/admin/logs`, { headers, params });
      setLogs(data);
    } catch (err) {
      console.error('Logs fetch failed:', err);
    }
  }, [logFilter]);

  const fetchJobs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/active-jobs`, { headers });
      setJobs(data);
    } catch (err) {
      console.error('Jobs fetch failed:', err);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/user-activity`, { headers });
      setUsers(data);
    } catch (err) {
      console.error('Users fetch failed:', err);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchHealth(), fetchLogs(), fetchJobs(), fetchUsers()]);
    setLoading(false);
  }, [fetchHealth, fetchLogs, fetchJobs, fetchUsers]);

  useEffect(() => { refreshAll(); }, []);
  useEffect(() => { fetchLogs(); }, [logFilter]);

  // Auto-refresh every 30s
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(refreshAll, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshAll]);

  const StatCard = ({ label, value, color, icon: Icon }) => (
    <div className="rounded-xl p-3 flex items-center gap-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div>
        <div className="text-lg font-bold text-white">{value}</div>
        <div className="text-[10px] text-white/40">{label}</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0B0F19] text-white" data-testid="admin-debug-panel">
      {/* Header */}
      <header className="h-12 px-4 flex items-center justify-between border-b border-white/[0.06]" style={{ background: 'rgba(11,15,25,0.9)', backdropFilter: 'blur(20px)' }}>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-white/5 rounded-lg">
            <ArrowLeft className="w-4 h-4 text-white/50" />
          </button>
          <img src="/assets/eipi-logo.png" alt="Eipicenter" className="h-4 w-auto brightness-0 invert opacity-70 cursor-pointer hover:opacity-100 transition-opacity" onClick={() => navigate('/')} title="Back to home" data-testid="admin-debug-logo-home" />
          <span className="text-sm font-medium text-white/70">Admin Debug Panel</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`p-1.5 rounded-lg text-[10px] flex items-center gap-1 ${autoRefresh ? 'text-green-400' : 'text-white/30'}`}
            title={autoRefresh ? 'Auto-refresh ON (30s)' : 'Auto-refresh OFF'}
          >
            {autoRefresh ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
          </button>
          <button onClick={refreshAll} disabled={loading} className="p-1.5 hover:bg-white/5 rounded-lg" data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 text-white/50 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </header>

      <div className="p-4 max-w-5xl mx-auto space-y-4">
        {/* Stat Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <StatCard label="Errors (1h)" value={health?.errors_1h ?? '—'} color={health?.errors_1h > 0 ? '#EF4444' : '#22C55E'} icon={AlertTriangle} />
          <StatCard label="Active Jobs" value={(health?.active_jobs ?? 0) + (jobs?.active_recipes?.length ?? 0)} color="#3B82F6" icon={Activity} />
          <StatCard label="Users" value={health?.stats?.users ?? '—'} color="#8B5CF6" icon={Users} />
          <StatCard label="System" value={health?.status === 'healthy' ? 'Healthy' : health?.status ?? '—'} color={health?.status === 'healthy' ? '#22C55E' : '#EF4444'} icon={Server} />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 rounded-lg p-1" style={{ background: 'rgba(255,255,255,0.03)' }}>
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-[11px] font-medium transition-all ${
                tab === t.id ? 'bg-white/[0.08] text-white' : 'text-white/30 hover:text-white/50'
              }`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{t.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {tab === 'errors' && (
          <div className="space-y-3">
            {/* Filters */}
            <div className="flex gap-2">
              <select
                value={logFilter.type}
                onChange={e => setLogFilter(f => ({ ...f, type: e.target.value }))}
                className="px-3 py-1.5 rounded-lg text-xs bg-white/[0.05] border border-white/[0.08] text-white"
                data-testid="log-type-filter"
              >
                <option value="">All Types</option>
                <option value="asset_error">Asset Errors</option>
                <option value="sketch_error">Sketch Errors</option>
                <option value="video_error">Video Errors</option>
                <option value="recipe_error">Recipe Errors</option>
                <option value="server_error">Server Errors</option>
                <option value="api_error">API Errors</option>
              </select>
              <select
                value={logFilter.hours}
                onChange={e => setLogFilter(f => ({ ...f, hours: parseInt(e.target.value) }))}
                className="px-3 py-1.5 rounded-lg text-xs bg-white/[0.05] border border-white/[0.08] text-white"
                data-testid="log-hours-filter"
              >
                <option value={1}>Last 1h</option>
                <option value={6}>Last 6h</option>
                <option value={24}>Last 24h</option>
                <option value={168}>Last 7d</option>
              </select>
              {logs?.total_by_type && Object.keys(logs.total_by_type).length > 0 && (
                <div className="flex gap-1.5 items-center ml-auto">
                  {Object.entries(logs.total_by_type).map(([type, count]) => (
                    <span key={type} className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: `${TYPE_COLORS[type] || '#666'}20`, color: TYPE_COLORS[type] || '#999' }}>
                      {type.replace('_error', '')}: {count}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Log List */}
            <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
              {logs?.logs?.length === 0 ? (
                <div className="p-8 text-center text-white/20 text-sm">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  No errors in the last {logFilter.hours}h
                </div>
              ) : (
                logs?.logs?.map((log, i) => (
                  <div
                    key={i}
                    className="px-3 py-2.5 border-b border-white/[0.04] hover:bg-white/[0.02] cursor-pointer"
                    onClick={() => setExpandedLog(expandedLog === i ? null : i)}
                  >
                    <div className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: SEVERITY_COLORS[log.severity] || '#666' }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: `${TYPE_COLORS[log.type] || '#666'}15`, color: TYPE_COLORS[log.type] || '#999' }}>
                            {log.type?.replace('_', ' ')}
                          </span>
                          <span className="text-[10px] text-white/25">{timeAgo(log.timestamp)}</span>
                        </div>
                        <p className="text-xs text-white/60 truncate">{log.message}</p>
                      </div>
                      <ChevronDown className={`w-3 h-3 text-white/20 flex-shrink-0 transition-transform ${expandedLog === i ? 'rotate-180' : ''}`} />
                    </div>
                    {expandedLog === i && log.context && Object.keys(log.context).length > 0 && (
                      <div className="mt-2 ml-3.5 p-2 rounded-lg text-[10px] text-white/40 space-y-0.5" style={{ background: 'rgba(255,255,255,0.02)' }}>
                        {Object.entries(log.context).map(([k, v]) => (
                          <div key={k}><span className="text-white/25">{k}:</span> <span className="text-white/50">{String(v)}</span></div>
                        ))}
                        <div><span className="text-white/25">timestamp:</span> <span className="text-white/50">{log.timestamp}</span></div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {tab === 'jobs' && (
          <div className="space-y-3">
            {/* Clear stuck runs button */}
            <div className="flex justify-end">
              <button
                onClick={async () => {
                  if (!window.confirm('Reset all runs stuck in processing for >30 min to failed?')) return;
                  try {
                    const { data } = await axios.post(`${API}/api/admin/clear-stuck-runs`, {}, { headers });
                    alert(`Cleared ${data.cleared} stuck runs`);
                    refreshAll();
                  } catch (err) {
                    alert('Failed: ' + (err.response?.data?.detail || err.message));
                  }
                }}
                className="px-3 py-1.5 rounded-lg text-[11px] font-medium text-amber-400 hover:bg-amber-500/10 transition-colors"
                style={{ border: '1px solid rgba(245,158,11,0.2)' }}
                data-testid="clear-stuck-btn"
              >
                Clear Stuck Runs (&gt;30m)
              </button>
            </div>
            <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
              {(!jobs?.active_projects?.length && !jobs?.active_recipes?.length) ? (
                <div className="p-8 text-center text-white/20 text-sm">
                  <Clock className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  No active jobs
                </div>
              ) : (
                <>
                  {jobs?.active_projects?.map((p, i) => (
                    <div key={i} className="px-3 py-3 border-b border-white/[0.04]">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-white/80">{p.name}</span>
                        <span className="text-[10px] text-teal-400">Stage {p.current_stage}</span>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {p.assets_processing && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400">Assets {p.assets_completed || 0}/{p.assets_total || '?'}</span>}
                        {p.sketches_processing && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-violet-500/15 text-violet-400">Sketches {p.sketches_completed || 0}/{p.sketches_total || '?'}</span>}
                        {p.keyframes_processing && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-400">Keyframes {p.keyframes_completed || 0}/{p.keyframes_total || '?'}</span>}
                        {p.videos_processing && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-400">Videos {p.videos_completed || 0}/{p.videos_total || '?'}</span>}
                        {p.prompts_processing && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-cyan-500/15 text-cyan-400">Prompts</span>}
                      </div>
                    </div>
                  ))}
                  {jobs?.active_recipes?.map((r, i) => (
                    <div key={`r${i}`} className="px-3 py-3 border-b border-white/[0.04]">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-white/80">{r.recipe_slug}</span>
                        <span className="text-[10px] text-teal-400">{r.progress_pct || 0}%</span>
                      </div>
                      <p className="text-[10px] text-white/40 mt-0.5">{r.current_node || 'Processing...'}</p>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        )}

        {tab === 'users' && (
          <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
            {users?.users?.map((u, i) => (
              <div key={i} className="px-3 py-2.5 border-b border-white/[0.04] flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white/80">{u.email}</span>
                    {u.is_admin && <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400 font-bold">ADMIN</span>}
                  </div>
                  <span className="text-[10px] text-white/30">{u.name || 'No name'}</span>
                </div>
                <div className="text-right">
                  <div className="text-[11px] text-white/50">{u.last_login ? timeAgo(u.last_login) : 'Never'}</div>
                  <div className="text-[9px] text-white/20">{u.approval_status || 'approved'}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'system' && health && (
          <div className="space-y-3">
            {/* Status */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-white/40">System Status</span>
                <span className={`text-xs font-bold ${health.status === 'healthy' ? 'text-green-400' : health.status === 'degraded' ? 'text-yellow-400' : 'text-red-400'}`}>
                  {health.status?.toUpperCase()}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-[11px]">
                <div><span className="text-white/30">Uptime:</span> <span className="text-white/60">{health.uptime}</span></div>
                <div><span className="text-white/30">Errors (24h):</span> <span className="text-white/60">{health.errors_24h}</span></div>
                <div><span className="text-white/30">Projects:</span> <span className="text-white/60">{health.stats?.projects}</span></div>
                <div><span className="text-white/30">Recipe Runs:</span> <span className="text-white/60">{health.stats?.recipe_runs}</span></div>
              </div>
            </div>

            {/* API Keys */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <span className="text-xs text-white/40 block mb-3">API Keys</span>
              <div className="space-y-2">
                {Object.entries(health.api_keys || {}).filter(([k]) => !k.includes('count')).map(([key, active]) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-[11px] text-white/50 capitalize">{key}</span>
                    <div className="flex items-center gap-1.5">
                      {key === 'gemini' && health.api_keys.gemini_keys_count !== undefined && (
                        <span className="text-[9px] text-white/25">({health.api_keys.gemini_keys_count} keys)</span>
                      )}
                      {active ? (
                        <span className="text-[10px] text-green-400 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Active</span>
                      ) : (
                        <span className="text-[10px] text-red-400 flex items-center gap-1"><XCircle className="w-3 h-3" /> Missing</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Concurrency */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <span className="text-xs text-white/40 block mb-3">Concurrency (Semaphores)</span>
              <div className="space-y-2">
                {Object.entries(health.concurrency || {}).map(([key, sem]) => {
                  const pct = sem.max ? ((sem.in_use / sem.max) * 100) : 0;
                  const color = pct > 80 ? '#EF4444' : pct > 50 ? '#F59E0B' : '#22C55E';
                  return (
                    <div key={key}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] text-white/50">{key.replace(/_/g, ' ')}</span>
                        <span className="text-[10px] text-white/30">{sem.in_use}/{sem.max}</span>
                      </div>
                      <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(pct, 2)}%`, background: color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
